import React, { Fragment, useState, useEffect } from 'react';
import {
  Box,
  CardMedia,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Switch,
  TextField,
  Typography,
  MenuItem,
  Autocomplete,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TablePagination,
  FormControlLabel
} from '@mui/material';
import { Close, DeleteForever, Edit, KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import Colors from '../../../Assets/Styles/Colors';
import BreadCrumbs from '../../../Components/BreadCrumbs/BreadCrumbs';
import { PrimaryButton } from '../../../Components/Buttons';
import { PhotoLibrary } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import CategoriesServices from '../../../Api/AdBazar/Categories/CategoriesServices';
import { baseUrl } from '../../../../axios';
import { useNavigate } from 'react-router-dom';
import { ErrorToaster, SuccessToaster } from '../../../Components/Toaster/Toaster';
import AdleaksServices from '../../../Api/AdLeak/AdleaksServices';
import { InputField } from '../../../Components/Fields/TextFields';
import SelectField from '../../../Components/Fields/SelectField';
import FreshLeaksServices from '../../../Api/Settings/FreshLeak/FreshLeaksServices';
import moment from 'moment';
import ConfirmationDialog from '../../../Components/Dialogs/ConfirmationDialog';
import styled from '@emotion/styled';

const tableHead = ["Leak Name", "Image", "Created At", "Status", "Action"];

const CustomSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.mode === 'dark' ? '#2ECA45' : Colors.primary,
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color:
        theme.palette.mode === 'light'
          ? theme.palette.grey[100]
          : theme.palette.grey[600],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

function TablePaginationActions(props) {
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        <KeyboardArrowRight />
      </IconButton>
    </Box>
  );
}

function UpdateFreshLeak({ open, handleClose, blogs, data, getAllFreshLeaks, options }) {
  const [updatedImage, setUpdatedImage] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [loading, setLoading] = useState(false);
  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm();
  const [label, setLabel] = useState("Active");
  const [isChecked, setIsChecked] = useState(null);

  const handleChecked = (e) => {
    setIsChecked(e.target.checked)
    setLabel(e.target.checked == true ? "Active" : "Inactive")
  }

  const handleUpdatedFileUpload = async (file) => {
    const formData = new FormData();
    formData.append("image", file);
    try {
      const result = await CategoriesServices.uploadImage(formData);
      setUpdatedImage(result?.data?.imageUrl)
    } catch (error) {
      console.log(error)
    }
  }

  const submitForm = async (formData) => {
    const obj = {
      name: formData?.updatedFreshLeakTitle,
      imageUrl: updatedImage,
      isActive: isChecked,
    }
    if (selectedOption?.id == 1) {
      obj.url = formData?.updatedUrl;
      obj.blogId = null;
    } else {
      obj.blogId = selectedBlog?.id;
      obj.url = "";
    }
    if (updatedImage == "") {
      ErrorToaster("Please select an image");
    } else {
      try {
        setLoading(true);
        const result = await FreshLeaksServices.updateFreshLeak(data?._id, obj);
        if (result.responseCode == 200) {
          SuccessToaster(result?.message);
          getAllFreshLeaks();
          reset();
          setUpdatedImage("");
          setSelectedBlog(null)
          handleClose();
        }
      } catch (e) {
        console.log(e);
      } finally {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    if (data) {
      setValue("updatedFreshLeakTitle", data?.name)
      setUpdatedImage(data?.imageUrl);
      setIsChecked(data?.isActive);
      if (data.blogId) {
        setSelectedOption({ id: 2, name: "Blog" })
        const selected = blogs.find((item) => item.id == data.blogId);
        setSelectedBlog(selected);
        setValue("updatedUrl", "")
      } else if (data?.url) {
        setSelectedOption({ id: 1, name: "URL" })
        setValue("updatedUrl", data?.url)
        setSelectedBlog(null);
      }
    }
  }, [open])

  return (
    <Dialog
      component={"form"}
      onSubmit={handleSubmit(submitForm)}
      open={open}
      onClose={handleClose}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "900px",
          },
        },
      }}
    >
      <DialogTitle>Update Fresh Leak</DialogTitle>
      <DialogContent sx={{ p: "20px 24px !important" }}>
        <Grid container columnSpacing={2}>
          <Grid item md={4.5}>
            <InputField
              register={register("updatedFreshLeakTitle", {
                required: "Please enter a title",
              })}
              error={errors?.updatedFreshLeakTitle?.message}
              helperText={errors?.updatedFreshLeakTitle?.message}
              label={"Leak Title"}
            />
          </Grid>
          <Grid item md={4.5}>
            <FormControl fullWidth>
              <SelectField
                setState={(item, name) => {
                  setSelectedOption({ id: item, name: name });
                }}
                stateValue={selectedOption?.name}
                data={options}
                label={"Select Option"}
              />
            </FormControl>
          </Grid>
          <Grid item md={3}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: "20px",
                mb: 2
              }}
            >
              <Typography>Status: </Typography>
              <FormControlLabel
                sx={{
                  m: 0,
                  gap: "10px"
                }}
                control={
                  <CustomSwitch
                    checked={isChecked}
                    onChange={handleChecked}
                  />
                }
                label={<Typography sx={{ width: "60px" }}>{label}</Typography>}
              />
            </Box>
          </Grid>
          {selectedOption && (
            selectedOption?.id == 1 ? (
              <Grid item md={12}>
                <Box>
                  <InputField
                    label={"Enter URL"}
                    register={register("updatedUrl", {
                      required: "Please enter an url"
                    })}
                    error={errors?.updatedUrl?.message}
                    helperText={errors?.updatedUrl?.message}
                  />
                </Box>
              </Grid>
            ) : (
              <Grid item md={12}>
                <Box sx={{ mb: 2 }}>
                  <FormControl fullWidth>
                    <Autocomplete
                      value={selectedBlog}
                      options={blogs}
                      renderOption={(props, option) =>
                        <MenuItem {...props} key={option.id} value={option.id}>
                          {option.title}
                        </MenuItem>
                      }
                      getOptionLabel={(option) => option.title}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setSelectedBlog(newValue);
                        }
                      }}
                      renderInput={(params) => <TextField {...params} label="Blog Title" />}
                    />
                  </FormControl>
                </Box>
              </Grid>
            )
          )}

          <Grid item md={12}>
            <Box
              sx={{
                height: "120px",
                border: updatedImage !== "" ? "none" : "1px solid rgba(0, 0, 0, 0.23)",
                borderRadius: "4px",
                mb: 2,
                ":hover": {
                  borderColor: updatedImage !== "" ? "transparent" : "#1F1F1F"
                }
              }}
            >
              {updatedImage !== "" ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    position: "relative",
                    height: "100%",
                  }}
                >
                  <CardMedia
                    component={"img"}
                    src={baseUrl + updatedImage}
                    sx={{
                      width: "120px",
                      height: "100%",
                      objectFit: "contain"
                    }}
                  />
                  <Box sx={{ position: "absolute", top: 0, right: 0 }}>
                    <IconButton sx={{ p: 0 }} onClick={() => setUpdatedImage("")}>
                      <Close sx={{ color: Colors.primary }} />
                    </IconButton>
                  </Box>
                </Box>
              ) : (
                <Fragment>
                  <InputLabel
                    htmlFor="updated-image-input"
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px"
                    }}
                  >
                    <PhotoLibrary sx={{ fontSize: "36px" }} />
                    <Typography>
                      Upload Image
                    </Typography>
                  </InputLabel>
                  <input
                    id="updated-image-input"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleUpdatedFileUpload(e.target.files[0])}
                    style={{ display: "none" }}
                  />
                </Fragment>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} sx={{ textTransform: "capitalize" }}>Cancel</Button>
        <PrimaryButton title={"Update"} loading={loading} type="submit" />
      </DialogActions>
    </Dialog>
  )
}

function FreshLeaks() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [count, setCount] = useState("");
  const [image, setImage] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [freshLeaks, setFreshLeaks] = useState([]);
  const [uOpen, setUOpen] = useState(false);
  const [cOpen, setCOpen] = useState(false);
  const [data, setData] = useState({});
  const [id, setId] = useState("");
  const [searchKey, setSearchKey] = useState("");
  const [selectedBlog, setSelectedBlog] = useState(null);
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const navigate = useNavigate();

  const options = [
    {
      _id: 1,
      name: "URL"
    },
    {
      _id: 2,
      name: "Blog"
    },
  ];

  const getAllBlogs = async () => {
    try {
      const result = await AdleaksServices.getAllAdLeaks(
        "",
        searchKey ? searchKey : "",
        1,
        25,
        "adleak",
        true
      )
      if (result.responseCode == 200) {
        const arrOfObj = result?.data?.blogs?.map((item) => ({ id: item?._id, title: item?.title }));
        setBlogs(arrOfObj);
      }
    } catch (error) {
      console.log(error);
    }
  }

  const handleFileUpload = async (file) => {
    const formData = new FormData();
    formData.append("image", file);
    try {
      const result = await CategoriesServices.uploadImage(formData);
      setImage(result?.data?.imageUrl)
    } catch (error) {
      console.log(error)
    }
  }

  const getAllFreshLeaks = async (page, limit) => {
    try {
      const result = await FreshLeaksServices.getAllFreshLeaks(
        page ? page : 1,
        limit ? limit : 10
      );
      // console.log("🚀 ~ getAllOffers ~ result:", result)
      setFreshLeaks(result?.data?.freshLeaks);
      setCount(result?.data?.totalLeaks);
    } catch (e) {
      console.log(e);
    }
  }

  const submitForm = async (formData) => {
    const obj = {
      name: formData?.leakTitle,
      imageUrl: image,
    }
    if (selectedOption?.id == 1) {
      obj.url = formData?.url
    } else {
      obj.blogId = selectedBlog?.id
    }
    if (image == "") {
      ErrorToaster("Please select an image");
    } else {
      try {
        setLoading(true);
        const result = await FreshLeaksServices.createFreshLeak(obj);
        if (result.responseCode == 200) {
          SuccessToaster(result?.message);
          reset();
          setImage("");
          setSelectedOption(null);
          getAllFreshLeaks();
          // console.log("🚀 ~ submitForm ~ result:", result)
        }
      } catch (e) {
        console.log(e)
      } finally {
        setLoading(false);
      }
    }
  }

  const handleClickUOpen = (data) => {
    setUOpen(true);
    setData(data);
  };

  const handleClickCOpen = (id) => {
    setCOpen(true);
    setId(id);
  };

  const handleCClose = () => {
    setCOpen(false);
  };

  const deleteFreshLeak = async () => {
    try {
      const result = await FreshLeaksServices.deleteFreshLeak(id);
      if (result?.responseCode == 200) {
        SuccessToaster(result?.message);
      }
    } catch (error) {
      console.log(error);
    }
    finally {
      handleCClose();
      getAllFreshLeaks();
    }
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    getAllFreshLeaks(newPage + 1, rowsPerPage)
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Math.floor(event.target.value));
    setPage(0);
    getAllFreshLeaks(page, event.target.value);
  };

  useEffect(() => {
    getAllBlogs();
    getAllFreshLeaks();
  }, [])

  return (
    <Fragment>
      <UpdateFreshLeak
        open={uOpen}
        handleClose={() => setUOpen(false)}
        options={options}
        blogs={blogs}
        data={data}
        getAllFreshLeaks={getAllFreshLeaks}
      />
      <ConfirmationDialog
        open={cOpen}
        onClose={handleCClose}
        action={deleteFreshLeak}
        id={id}
        title={"Delete Fresh Leak"}
        btn={"Delete"}
      />
      <Box
        sx={{
          borderRadius: "12px",
        }}
      >
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{
            background: Colors.primaryGradient,
            p: 2,
            borderRadius: "12px",
          }}
        >
          <Grid item xs={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <IconButton onClick={() => navigate(-1)}>
                <KeyboardArrowLeft sx={{ color: Colors.white }} />
              </IconButton>
              <Typography variant="body1" fontWeight="bold" sx={{ color: Colors.white }}>
                Create Fresh Leak
              </Typography>
            </Box>
          </Grid>
          <Grid
            item
            xs={6}
            sx={{ display: "flex", justifyContent: "flex-end" }}
          >
            <BreadCrumbs currentPage={["Create Fresh Leak"]} />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, p: 2, bgcolor: Colors.white, borderRadius: "12px" }}>
          <Grid container spacing={2} component={"form"} onSubmit={handleSubmit(submitForm)}>
            <Grid item md={6}>
              <Box>
                <InputField
                  label={"Leak Title"}
                  register={register("leakTitle", {
                    required: "Please enter a title"
                  })}
                  error={errors?.leakTitle?.message}
                  helperText={errors?.leakTitle?.message}
                />
              </Box>
            </Grid>

            <Grid item md={6}>
              <FormControl fullWidth>
                <SelectField
                  setState={(item, name) => {
                    setSelectedOption({ id: item, name: name });
                  }}
                  stateValue={selectedOption?.name}
                  data={options}
                  label={"Select Option"}
                />
              </FormControl>
            </Grid>

            {selectedOption && (
              selectedOption?.id == 1 ? (
                <Grid item md={12}>
                  <Box>
                    <InputField
                      label={"Enter URL"}
                      register={register("url", {
                        required: "Please enter an url"
                      })}
                      error={errors?.url?.message}
                      helperText={errors?.url?.message}
                    />
                  </Box>
                </Grid>
              ) : (
                <Grid item md={12}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={blogs}
                      renderOption={(props, option) =>
                        <MenuItem {...props} key={option.id} value={option.id}>
                          {option.title}
                        </MenuItem>
                      }
                      getOptionLabel={(option) => option.title}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setSelectedBlog(newValue);
                        }
                      }}
                      renderInput={(params) => <TextField {...params} label="Blog Title" />}
                    />
                  </FormControl>
                </Grid>
              )
            )}

            <Grid item md={12}>
              <Box
                sx={{
                  height: "120px",
                  border: image !== "" ? "none" : "1px solid rgba(0, 0, 0, 0.23)",
                  borderRadius: "4px",
                  mb: 2,
                  ":hover": {
                    borderColor: image !== "" ? "transparent" : "#1F1F1F"
                  }
                }}
              >
                {image !== "" ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      position: "relative",
                      height: "100%",
                    }}
                  >
                    <CardMedia
                      component={"img"}
                      src={baseUrl + image}
                      sx={{
                        width: "120px",
                        height: "100%",
                        objectFit: "contain"
                      }}
                    />
                    <Box sx={{ position: "absolute", top: 0, right: 0 }}>
                      <IconButton sx={{ p: 0 }} onClick={() => setImage("")}>
                        <Close sx={{ color: Colors.primary }} />
                      </IconButton>
                    </Box>
                  </Box>
                ) : (
                  <Fragment>
                    <InputLabel
                      htmlFor="image-input"
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "10px"
                      }}
                    >
                      <PhotoLibrary sx={{ fontSize: "36px" }} />
                      <Typography>
                        Upload Image
                      </Typography>
                    </InputLabel>
                    <input
                      id="image-input"
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e.target.files[0])}
                      style={{ display: "none" }}
                    />
                  </Fragment>
                )}
              </Box>
            </Grid>
            <Grid item md={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end"
                }}
              >
                <PrimaryButton
                  title={"Submit"}
                  type={"submit"}
                  loading={loading}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        <TableContainer
          component={Paper}
          sx={{
            "&::-webkit-scrollbar": {
              bgcolor: Colors.white,
              height: "8px",
              borderRadius: "10px",
              cursor: "pointer",
            },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: Colors.primary,
              borderRadius: "10px",
              cursor: "pointer",
            },
            "&.MuiPaper-root": {
              borderRadius: "12px",
              mt: 2,
              boxShadow: 0,
            },
          }}
        >
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow sx={{ bgcolor: Colors.lightGray }}>
                {tableHead.map((item, index) => (
                  <TableCell key={index}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      {item}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {freshLeaks.map((row, index) => (
                <TableRow key={index}>
                  <TableCell component="th" scope="row">
                    {row?.name}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    <Box>
                      <CardMedia
                        component={"img"}
                        src={baseUrl + row?.imageUrl}
                        sx={{ width: "60px", height: "60px", objectFit: "contain", borderRadius: "50%" }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {row?.cteatedAt ? moment(row?.cteatedAt).format("DD-MM-YYYY") : moment().format("DD-MM-YYYY")}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    <Chip
                      label={row?.isActive ? 'Active' : 'Inactive'}
                      sx={{
                        bgcolor: row?.isActive ? Colors.success + 26 : Colors.danger + 26,
                        color: row?.isActive ? Colors.success : Colors.danger,
                        height: 26,
                        textTransform: 'capitalize',
                      }}
                    />
                  </TableCell>
                  <TableCell component="th" scope="row">
                    <Box display="flex">
                      <Tooltip title="View">
                        <IconButton
                          onClick={() => {
                            handleClickUOpen(row)
                          }}
                        >
                          <Edit sx={{ color: Colors.success }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => {
                            handleClickCOpen(row._id)
                          }}
                        >
                          <DeleteForever sx={{ color: Colors.primary }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Table sx={{ marginTop: "12px" }}>
          <TablePagination
            sx={{
              borderBottom: "none",
              bgcolor: Colors.white,
              borderRadius: "12px",
            }}
            rowsPerPageOptions={[10, 20, 30]}
            colSpan={12}
            count={+count}
            rowsPerPage={rowsPerPage}
            page={page}
            SelectProps={{
              inputProps: {
                "aria-label": "rows per page",
              },
              native: true,
            }}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
          />
        </Table>
      </Box>
    </Fragment >
  )
}

export default FreshLeaks